import React, { useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, PrinterIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { User } from '../../types';
import Button from '../common/Button';
import { resolveImageUrl } from '../../utils/image';

interface CompCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User;
}

const CompCardModal: React.FC<CompCardModalProps> = ({ isOpen, onClose, user }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [selectedImageIndex, setSelectedImageIndex] = React.useState<number | null>(null);

  if (!isOpen) return null;

  // Extract stats
  const mp = user.model_profile;
  const heightStr = mp?.height
    ? `${mp.height} cm (${Math.floor((mp.height / 2.54) / 12)}'${Math.round((mp.height / 2.54) % 12)}")`
    : 'N/A';
  const weightStr = mp?.weight
    ? `${mp.weight} kg (${Math.round(mp.weight * 2.20462)} lbs)`
    : 'N/A';
  const bustStr = mp?.bust
    ? `${mp.bust} cm (${Math.round(mp.bust / 2.54)}")`
    : 'N/A';
  const waistStr = mp?.waist
    ? `${mp.waist} cm (${Math.round(mp.waist / 2.54)}")`
    : 'N/A';
  const hipsStr = mp?.hips
    ? `${mp.hips} cm (${Math.round(mp.hips / 2.54)}")`
    : 'N/A';

  // Get portfolio images for card. We need a primary headshot + 3-4 other images.
  // Use profile_pic_signed as the first fallback to ensure CloudFront pre-signed URL is used.
  const profilePic = user.profile_pic_signed || user.profile_pic_medium_signed || user.profile_pic || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?q=80&w=600&auto=format&fit=crop';
  
  // Use digitals or recent portfolio images as supporting card images
  const supportImages = mp?.digitals && mp.digitals.length > 0
    ? [...mp.digitals.slice(0, 4)]
    : [...(user.recent_images_signed || user.recent_images || []).slice(0, 4)];

  const allImages = [profilePic, ...supportImages.slice(0, 4)];

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      <AnimatePresence>
        <div 
          className="fixed inset-0 z-[9999] overflow-y-auto bg-black/60 flex items-start sm:items-center justify-center p-2 sm:p-4 backdrop-blur-sm cursor-pointer"
          onClick={onClose}
        >
          {/* Style block for perfect single page print layout */}
          <style>{`
            #print-comp-card-portal {
              display: none !important;
            }
            @media print {
              @page {
                size: A4 portrait;
                margin: 10mm 10mm 10mm 10mm !important;
              }
              html, body {
                height: auto !important;
                min-height: 0 !important;
                overflow: visible !important;
                margin: 0 !important;
                padding: 0 !important;
                background: white !important;
                color: black !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              /* Hide all direct body elements except the portal print box */
              body > *:not(#print-comp-card-portal) {
                display: none !important;
              }
              /* Render only the print portal */
              body > #print-comp-card-portal {
                display: block !important;
                position: relative !important;
                left: 0 !important;
                top: 0 !important;
                width: 100% !important;
                height: auto !important;
                overflow: visible !important;
                max-width: 190mm !important;
                margin: 0 auto !important;
                background: white !important;
                color: black !important;
                box-shadow: none !important;
                border: none !important;
                padding: 0 !important;
                page-break-inside: avoid !important;
                break-inside: avoid !important;
              }
              /* Force solid black text and borders in print */
              #print-comp-card-portal, #print-comp-card-portal * {
                color: black !important;
                border-color: black !important;
              }
              /* Print layout definitions */
              .print-header-layout {
                display: flex !important;
                justify-content: space-between !important;
                align-items: flex-end !important;
                border-bottom: 2px solid black !important;
                padding-bottom: 12px !important;
                margin-bottom: 16px !important;
                width: 100% !important;
              }
              .print-title {
                font-size: 22px !important;
                font-weight: 900 !important;
                text-transform: uppercase !important;
                letter-spacing: -0.02em !important;
                color: black !important;
                margin: 0 !important;
                line-height: 1 !important;
              }
              .print-subtitle {
                font-size: 9px !important;
                font-weight: bold !important;
                text-transform: uppercase !important;
                letter-spacing: 0.2em !important;
                color: #4b5563 !important;
                margin-top: 6px !important;
                display: block !important;
              }
              .print-verified-badge {
                font-size: 9px !important;
                font-weight: 900 !important;
                text-transform: uppercase !important;
                letter-spacing: 0.15em !important;
                color: #0d9488 !important;
                border: 1px solid #0d9488 !important;
                border-radius: 4px !important;
                padding: 2px 8px !important;
              }
              .print-main-grid {
                display: grid !important;
                grid-template-columns: repeat(12, minmax(0, 1fr)) !important;
                gap: 24px !important;
                width: 100% !important;
              }
              .print-col-7 {
                grid-column: span 7 / span 7 !important;
              }
              .print-col-5 {
                grid-column: span 5 / span 5 !important;
              }
              .print-headshot-box {
                height: 120mm !important;
                width: 100% !important;
                border-radius: 8px !important;
                overflow: hidden !important;
                background-color: #f3f4f6 !important;
                border: 1px solid #e5e7eb !important;
              }
              .print-name-title {
                font-size: 24px !important;
                font-weight: 900 !important;
                text-transform: uppercase !important;
                letter-spacing: -0.02em !important;
                color: black !important;
                line-height: 1.1 !important;
                margin: 0 !important;
              }
              .print-agency-title {
                font-size: 10px !important;
                font-weight: 700 !important;
                text-transform: uppercase !important;
                letter-spacing: 0.1em !important;
                color: #4b5563 !important;
                margin-top: 4px !important;
                margin-bottom: 0 !important;
              }
              .print-support-grid {
                display: grid !important;
                grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
                gap: 12px !important;
              }
              .print-support-img-box {
                height: 50mm !important;
                width: 100% !important;
                border-radius: 8px !important;
                overflow: hidden !important;
                background-color: #f3f4f6 !important;
                border: 1px solid #e5e7eb !important;
              }
              .print-specs-box {
                border-top: 2px solid black !important;
                padding-top: 16px !important;
              }
              .print-specs-title {
                font-size: 12px !important;
                font-weight: 900 !important;
                text-transform: uppercase !important;
                letter-spacing: 0.1em !important;
                color: black !important;
                margin-top: 0 !important;
                margin-bottom: 12px !important;
              }
              .print-specs-grid {
                display: grid !important;
                grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
                gap: 8px 12px !important;
                font-size: 11px !important;
                font-weight: 700 !important;
                color: #1f2937 !important;
              }
              .print-footer-box {
                border-top: 1px solid #e5e7eb !important;
                padding-top: 16px !important;
                font-size: 9px !important;
                font-weight: 700 !important;
                color: #4b5563 !important;
                line-height: 1.4 !important;
              }
            }
          `}</style>

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="bg-white dark:bg-gray-900 border border-gray-150 dark:border-gray-800 rounded-3xl w-full max-w-4xl max-h-none sm:max-h-[90vh] flex flex-col shadow-2xl overflow-hidden my-4 sm:my-auto cursor-default"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Responsive Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 sm:p-6 border-b border-gray-100 dark:border-gray-800 gap-4 sm:gap-0">
              <div>
                <h2 className="text-lg sm:text-xl font-extrabold text-gray-950 dark:text-white">Composite Card</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Zed Card preview. Standard formatting for agency castings.</p>
              </div>
              <div className="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-end">
                <Button variant="outline" size="sm" onClick={handlePrint} className="flex items-center gap-2 whitespace-nowrap">
                  <PrinterIcon className="w-4 h-4" />
                  Print / Save PDF
                </Button>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors text-gray-500"
                >
                  <XMarkIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>
            </div>

            {/* Card Container */}
            <div className="flex-1 overflow-y-auto p-3 sm:p-6 bg-gray-50 dark:bg-gray-950 flex justify-center">
              
              <div 
                ref={cardRef} 
                className="w-full max-w-[720px] bg-white border border-gray-200 shadow-md p-4 sm:p-6 grid grid-cols-1 md:grid-cols-12 gap-5 sm:gap-6 text-black print:border-0 print:shadow-none"
              >
                {/* Left Column: Big Headshot */}
                <div className="md:col-span-7 flex flex-col justify-between">
                  <div 
                    className="aspect-[3/4] w-full rounded-lg overflow-hidden border border-gray-150 bg-gray-50 cursor-pointer hover:opacity-95 transition-opacity"
                    onClick={() => setSelectedImageIndex(0)}
                  >
                    <img src={resolveImageUrl(profilePic)} alt="Headshot" className="w-full h-full object-cover" />
                  </div>
                  
                  {/* Big Logo / Name block on left */}
                  <div className="mt-4">
                    <h1 className="text-2xl sm:text-3xl font-extrabold uppercase tracking-tighter text-gray-950 leading-tight">
                      {user.first_name} {user.last_name}
                    </h1>
                    <p className="text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-widest mt-1">
                      {mp?.agency_represented ? `Represented by: ${mp.agency_name}` : 'Independent Modeling Talent'}
                    </p>
                  </div>
                </div>

                {/* Right Column: Grid of Supporting Images & Stats */}
                <div className="md:col-span-5 flex flex-col justify-between space-y-5 sm:space-y-6 mt-4 md:mt-0">
                  
                  {/* Support Image Grid */}
                  {supportImages.length > 0 && (
                    <div className="grid grid-cols-2 gap-2 sm:gap-3">
                      {supportImages.slice(0, 4).map((url, idx) => (
                        <div 
                          key={idx} 
                          className="aspect-[3/4] rounded-lg overflow-hidden border border-gray-150 bg-gray-50 cursor-pointer hover:opacity-95 transition-opacity"
                          onClick={() => setSelectedImageIndex(idx + 1)}
                        >
                          <img src={resolveImageUrl(url)} alt={`Look ${idx + 1}`} className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Measurements Block */}
                  <div className="border-t-2 border-black pt-4 space-y-2">
                    <h3 className="text-xs font-black uppercase tracking-widest text-black mb-2">Specifications</h3>
                    
                    <div className="grid grid-cols-2 gap-y-1.5 sm:gap-y-2 text-xs font-bold tracking-tight text-gray-800">
                      <div>Height:</div>
                      <div className="text-black text-right">{heightStr}</div>
                      
                      <div>Weight:</div>
                      <div className="text-black text-right">{weightStr}</div>

                      <div>Bust / Chest:</div>
                      <div className="text-black text-right">{bustStr}</div>

                      <div>Waist:</div>
                      <div className="text-black text-right">{waistStr}</div>

                      <div>Hips:</div>
                      <div className="text-black text-right">{hipsStr}</div>

                      <div>Shoe Size:</div>
                      <div className="text-black text-right">{mp?.shoe_size || 'N/A'}</div>

                      <div>Dress Size:</div>
                      <div className="text-black text-right">{mp?.dress_size || 'N/A'}</div>

                      <div>Hair / Eyes:</div>
                      <div className="text-black text-right">
                        {mp?.hair_color || 'N/A'} / {mp?.eye_color || 'N/A'}
                      </div>
                    </div>
                  </div>

                  {/* Contact/Agency Footer */}
                  <div className="border-t border-gray-200 pt-4 text-[10px] text-gray-500 font-semibold space-y-1">
                    {user.instagram_handle && <div>INSTAGRAM: @{user.instagram_handle.replace('@', '')}</div>}
                    {user.website && <div>WEBSITE: {user.website}</div>}
                    {user.location && <div>LOCATION: {user.location}</div>}
                    <div className="text-[9px] text-gray-400 mt-2 font-medium">Generated via ClickCollab Platform</div>
                  </div>

                </div>

              </div>

            </div>
          </motion.div>
        </div>
      </AnimatePresence>

      {/* Fullscreen Image Preview Lightbox */}
      <AnimatePresence>
        {selectedImageIndex !== null && (
          <div 
            className="fixed inset-0 z-[10000] bg-black/95 flex items-center justify-center p-4 backdrop-blur-md select-none cursor-default"
            onClick={() => setSelectedImageIndex(null)}
          >
            {/* Close button */}
            <button
              onClick={() => setSelectedImageIndex(null)}
              className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors z-50 shadow-md"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>

            {/* Left navigation arrow */}
            {selectedImageIndex > 0 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedImageIndex(prev => prev !== null ? prev - 1 : null);
                }}
                className="absolute left-4 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors z-50 shadow-md"
              >
                <ChevronLeftIcon className="w-6 h-6" />
              </button>
            )}

            {/* Right navigation arrow */}
            {selectedImageIndex < allImages.length - 1 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedImageIndex(prev => prev !== null ? prev + 1 : null);
                }}
                className="absolute right-4 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors z-50 shadow-md"
              >
                <ChevronRightIcon className="w-6 h-6" />
              </button>
            )}

            {/* Active image container */}
            <motion.div
              key={selectedImageIndex}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="relative max-w-full max-h-[85vh] flex flex-col items-center gap-4"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={resolveImageUrl(allImages[selectedImageIndex])}
                alt={`Preview Look ${selectedImageIndex + 1}`}
                className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl border border-white/5"
              />
              <div className="text-center text-white/75 text-xs font-black uppercase tracking-widest mt-2">
                {selectedImageIndex === 0 ? 'Primary Headshot' : `Look ${selectedImageIndex}`} (Image {selectedImageIndex + 1} of {allImages.length})
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {createPortal(
        <div id="print-comp-card-portal">
          {/* Header */}
          <div className="print-header-layout">
            <div>
              <h2 className="print-title">Model Composite Card</h2>
              <span className="print-subtitle">Via ClickCollab Casting</span>
            </div>
            <div className="print-header-right">
              <span className="print-verified-badge">Verified Talent Profile</span>
            </div>
          </div>

          {/* Main Layout Grid */}
          <div className="print-main-grid">
            {/* Left Column */}
            <div className="print-col-7 flex flex-col justify-between">
              <div className="print-headshot-box">
                <img src={resolveImageUrl(profilePic)} alt="Headshot" className="w-full h-full object-cover" />
              </div>
              <div className="mt-4">
                <h1 className="print-name-title">{user.first_name} {user.last_name}</h1>
                <p className="print-agency-title">{mp?.agency_represented ? `Represented by: ${mp.agency_name}` : 'Independent Modeling Talent'}</p>
              </div>
            </div>

            {/* Right Column */}
            <div className="print-col-5 flex flex-col justify-between space-y-4">
              {/* Support Images */}
              {supportImages.length > 0 && (
                <div className="print-support-grid">
                  {supportImages.slice(0, 4).map((url, idx) => (
                    <div key={idx} className="print-support-img-box">
                      <img src={resolveImageUrl(url)} className="w-full h-full object-cover" alt="" />
                    </div>
                  ))}
                </div>
              )}

              {/* Specifications */}
              <div className="print-specs-box">
                <h3 className="print-specs-title">Specifications</h3>
                <div className="print-specs-grid">
                  <div>Height:</div><div className="text-right font-black">{heightStr}</div>
                  <div>Weight:</div><div className="text-right font-black">{weightStr}</div>
                  <div>Bust / Chest:</div><div className="text-right font-black">{bustStr}</div>
                  <div>Waist:</div><div className="text-right font-black">{waistStr}</div>
                  <div>Hips:</div><div className="text-right font-black">{hipsStr}</div>
                  <div>Shoe Size:</div><div className="text-right font-black">{mp?.shoe_size || 'N/A'}</div>
                  <div>Dress Size:</div><div className="text-right font-black">{mp?.dress_size || 'N/A'}</div>
                  <div>Hair / Eyes:</div><div className="text-right font-black">{mp?.hair_color || 'N/A'} / {mp?.eye_color || 'N/A'}</div>
                </div>
              </div>

              {/* Footer contact */}
              <div className="print-footer-box">
                {user.instagram_handle && <div>INSTAGRAM: @{user.instagram_handle.replace('@', '')}</div>}
                {user.website && <div>WEBSITE: {user.website}</div>}
                {user.location && <div>LOCATION: {user.location}</div>}
                <div style={{ fontSize: '8px', color: '#9ca3af', marginTop: '8px' }}>Generated via ClickCollab Platform</div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default CompCardModal;
